import { Navigate, useParams } from 'react-router-dom';
import { HubLayout } from '@/components/layout/HubLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { OrganizationSettingsPage } from '@/pages/org/OrganizationSettingsPage';
import { ServiceAccountsPage } from '@/pages/org/ServiceAccountsPage';
import {
  DEFAULT_ORGANIZATION_HUB_SECTION,
  ORGANIZATION_HUB_SECTIONS,
  organizationHubSection,
} from './organization-hub-config';

function OrganizationHubContent({
  section,
  serviceAccountId,
}: {
  section: string;
  serviceAccountId?: string;
}) {
  if (section === 'service-accounts') {
    // Detail is selected via the `:id` route param; the list page switches
    // in place when it is present.
    void serviceAccountId;
    return <ServiceAccountsPage embedded />;
  }

  return <OrganizationSettingsPage embedded />;
}

/**
 * Host-owned Organization hub: General settings and Service accounts in one
 * left-rail layout (same pattern as Agents / MCP / Credentials).
 */
export function OrganizationHubPage() {
  const slug = useActiveOrgSlug();
  const { section: sectionParam, id: serviceAccountId } = useParams<{
    section?: string;
    id?: string;
  }>();

  if (!slug) {
    return <Navigate to={ROUTES.settings} replace />;
  }

  const orgRoutes = ROUTES.org(slug);
  // `/service-accounts/:id` does not bind `:section`; treat it as the
  // service-accounts section when an id is present.
  const section = serviceAccountId
    ? 'service-accounts'
    : sectionParam !== undefined && organizationHubSection(sectionParam)
      ? sectionParam
      : 'general';

  if (
    !serviceAccountId &&
    sectionParam !== undefined &&
    organizationHubSection(sectionParam) === undefined
  ) {
    return (
      <Navigate to={orgRoutes.organizationHubSection(DEFAULT_ORGANIZATION_HUB_SECTION)} replace />
    );
  }

  const activeSection = organizationHubSection(section);

  return (
    <HubLayout
      title="Organization"
      description="General settings and service accounts for this organization."
      sections={ORGANIZATION_HUB_SECTIONS}
      activeSectionId={section}
      sectionHref={(item) => orgRoutes.organizationHubSection(item.id)}
      navLabel="Organization sections"
    >
      <HubReadGuard permission={activeSection?.permission}>
        <OrganizationHubContent section={section} serviceAccountId={serviceAccountId} />
      </HubReadGuard>
    </HubLayout>
  );
}
